import dayjs from 'dayjs'
import { BlockCategory } from 'src/forms/AdsForm/types'

import type {
  SClassified,
  SClassifiedAlert,
  SClassifiedAnswer,
  SClassifiedCabinetType,
  SClassifiedCategory,
  SClassifiedEmitter,
  SClassifiedMedia,
  SClassifiedOfferType,
  SClassifiedSecretariat,
  SClassifiedSpecialty,
} from '../types/api/Classified'
import type {
  Classified,
  ClassifiedAlert,
  ClassifiedAnswer,
  ClassifiedCabinetType,
  ClassifiedCategory,
  ClassifiedEmitter,
  ClassifiedMedia,
  ClassifiedOfferType,
  ClassifiedSecretariat,
  ClassifiedSpecialty,
} from '../types/Classified'
import Router from '../../routes/Router'
import routes from '../../routes/definitions'
import { AdsCardProps } from '../../components/AdsCard'
import { AdsCardVariant } from '../../components/AdsCard/styled'
import { SClassifiedView } from '../types/api/ClassifiedView'
import { getCharacters } from '../../helpers/StringHelpers'
import { MediaType } from '../../form/fields/FormFieldUploadMedia'
import { ImageTransformer } from '../../relay/Transformers/ImageTransformer'

import { userTransformer } from './userTransformer'
import { imageTransformer } from './imageTransformer'

const idOf = (o: any): string =>
  o?.documentId ?? (o?.id != null ? String(o.id) : '')

export const classifiedTransformer = (data: SClassified): Classified => {
  // @ts-ignore
  return {
    ...data,
    id: idOf(data),
    numericId: data?.id,
    status: data?.classifiedStatus ?? null,
    ...(data?.emitter && {
      emitter: classifiedEmitterTransformer(data.emitter),
    }),
    ...(data?.classifiedCategory && {
      classifiedCategory: classifiedCategoryTransformer(
        data.classifiedCategory
      ),
    }),
    ...(data?.classified_answers && {
      classified_answers: data.classified_answers.map((a) =>
        ClassifiedAnswerTransformer(a)
      ),
    }),
    ...(data?.user && {
      user: userTransformer(data.user),
    }),
    ...(data?.classified_views && {
      classified_views: data.classified_views.map((v) =>
        classifiedViewTransformer(v)
      ),
      views: String(data.classified_views.length),
    }),
    ...(data?.medias && {
      medias: data.medias.map((m) => classifiedMediaTransformer(m)),
    }),
    ...(data?.block?.length
      ? {
          block: data.block.map((block: any) => {
            return {
              ...block,
              offerType: block?.offerType
                ? classifiedOfferTypeTransformer(block.offerType)
                : null,
              ...(block?.cabinetType && {
                cabinetType: classifiedCabinetTypeTransformer(
                  block.cabinetType
                ),
              }),
              ...(block?.specialities && {
                specialities: (Array.isArray(block.specialities)
                  ? block.specialities
                  : []
                ).map((specialty: any) =>
                  classifiedSpecialtyTransformer(specialty)
                ),
              }),
              ...(block?.secretariats && {
                secretariats: (Array.isArray(block.secretariats)
                  ? block.secretariats
                  : []
                ).map((secretariat: any) =>
                  classifiedSecretariatTransformer(secretariat)
                ),
              }),
              ...(block?.speciality
                ? {
                    speciality: classifiedSpecialtyTransformer(
                      block.speciality
                    ),
                  }
                : {}),
            }
          }),
        }
      : { block: [] }),
  }
}

export const classifiedCategoryTransformer = (
  data: SClassifiedCategory
): ClassifiedCategory => {
  return {
    ...data,
    id: idOf(data),
    classifieds:
      data?.classifieds?.map((item) => classifiedTransformer(item)) ?? [],
  } as ClassifiedCategory
}

export const ClassifiedAnswerTransformer = (
  data: SClassifiedAnswer
): ClassifiedAnswer => {
  return {
    ...data,
    id: idOf(data),
    numericId: data?.id,
    ...(data?.studentCard && {
      studentCard: imageTransformer(data.studentCard),
    }),
    ...(data?.document && {
      document: imageTransformer(data.document),
    }),
    ...(data?.classified && {
      classified: classifiedTransformer(data.classified),
    }),
    ...(data?.answer_user && {
      answer_user: userTransformer(data.answer_user),
    }),
  } as ClassifiedAnswer
}

export const classifiedOfferTypeTransformer = (
  data: SClassifiedOfferType
): ClassifiedOfferType => ({
  id: idOf(data),
  name: data.name,
})

export const classifiedSecretariatTransformer = (
  data: SClassifiedSecretariat
): ClassifiedSecretariat => ({
  id: idOf(data),
  name: data.name,
})

export const classifiedSpecialtyTransformer = (
  data: SClassifiedSpecialty
): ClassifiedSpecialty => ({
  id: idOf(data),
  name: data.name,
})

export const classifiedMediaTransformer = (
  data: SClassifiedMedia
): ClassifiedMedia => {
  return {
    ...data,
    id: idOf(data),
  } as ClassifiedMedia
}

export const classifiedCabinetTypeTransformer = (
  data: SClassifiedCabinetType
): ClassifiedCabinetType => ({
  id: idOf(data),
  name: data.name,
})

export const classifiedEmitterTransformer = (
  data: SClassifiedEmitter
): ClassifiedEmitter => {
  return {
    ...data,
    id: idOf(data),
    ...(data.classifieds && {
      classifieds: data.classifieds.map((c) => classifiedTransformer(c)),
    }),
    ...(data.user && {
      user: userTransformer(data.user),
    }),
  } as ClassifiedEmitter
}

export const classifiedViewTransformer = (view: SClassifiedView) => ({
  id: idOf(view),
  ...(view.user && {
    user: userTransformer(view.user),
  }),
  ...(view.classified && {
    classified: classifiedTransformer(view.classified),
  }),
})

export const classifiedAlertTransformer = (
  data: SClassifiedAlert
): ClassifiedAlert => {
  return {
    ...data,
    id: idOf(data),
    ...(data.categories && {
      categories: data.categories.map((category) =>
        classifiedCategoryTransformer(category)
      ),
    }),
    ...(data.user && {
      user: userTransformer(data.user),
    }),
    ...(data.specialities && {
      specialities: data.specialities.map((speciality) =>
        classifiedCategoryTransformer(speciality as any)
      ),
    }),
  } as ClassifiedAlert
}

const getYoutubeId = (url?: string): string | undefined =>
  url?.match(/(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/)([\w-]{11})/)?.[1]

const classifiedCardMedia = (data: Classified): AdsCardProps['media'] => {
  if (data?.mainMediaType === MediaType.Photo && data?.image) {
    return ImageTransformer(data.image as any, true)
  }
  if (data?.mainMediaType === MediaType.Video && data?.videoUrl) {
    const youtubeId = getYoutubeId(data.videoUrl)
    return {
      src: youtubeId
        ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
        : '/static/assets/images/video_preview.jpg',
      alt: '',
    }
  }
  return undefined
}

export const classifiedCardTransformer = ({
  data,
  t,
  colorVariant,
  contentVariant,
  latestPublishLabel,
  withCounters,
}: {
  data: Classified
  t: any
  colorVariant?: AdsCardVariant
  contentVariant?: 'default' | 'withoutLink'
  latestPublishLabel?: string
  withCounters?: boolean
}): AdsCardProps => {
  return {
    title: data?.title ?? '',
    ...(data.classifiedCategory?.form === BlockCategory.Liberal &&
      data.user?.speciality?.name && {
        title: `${data.title} (${data.user?.speciality.name})`,
      }),
    publicationDate: `${
      latestPublishLabel ?? t('block_ads_related_publish_label')
    } ${dayjs(data.updatedAt ?? data.createdAt).format('D MMMM YYYY')}`,
    ...(data?.city &&
      data?.postalCode && {
        localization: t('city_postal_code_formated', {
          city: data?.city,
          postalCode: getCharacters(data?.postalCode ?? '', 0, 5),
        }),
      }),
    offerType: data?.classifiedCategory?.name ?? '',
    description: data?.summary,
    layout: 'grid',
    media: classifiedCardMedia(data),
    ...(data?.classifiedCategory?.form === BlockCategory.Liberal && {
      offerTypeLink: t('ads_intro_link_href_liberal'),
    }),
    ...(data?.classifiedCategory?.form === BlockCategory.Adopt && {
      offerTypeLink: t('ads_intro_link_href_adopt'),
    }),
    ...(data?.classifiedCategory?.form === BlockCategory.Job && {
      offerTypeLink: t('ads_intro_link_href_job'),
    }),
    ...(contentVariant !== 'withoutLink' && {
      link: {
        href: Router.getRouteUrl(routes.classified, {
          slug: data?.slug,
        }),
        text: t('more_information_button_text'),
      },
    }),
    ...(withCounters && {
      adViewsNumber: String(data?.classified_views?.length ?? 0),
      adCommentNumber: String(data?.classified_answers?.length ?? 0),
    }),
    variant: colorVariant ?? AdsCardVariant.primary,
  }
}
